import { useId, useRef, useState } from 'react';
import { peso } from '../api/documenti.js';
import { t } from '../i18n/lingua.js';

/**
 * Dove si trascina un file.
 *
 * Trascinare è il modo con cui il v2 carica gli allegati e la gente ci si è
 * abituata; ma trascinare non funziona da tastiera e non funziona su un
 * portatile senza mouse, quindi la zona è anche un pulsante — un clic apre
 * la finestra di scelta, e col tasto Invio pure.
 *
 * Il controllo sull'estensione si fa qui **e** dalla parte del servizio:
 * questo serve a dire subito che il file non va bene, quello serve perché
 * questo si può aggirare.
 */
export function ZonaFile({
  file,
  estensioni,
  massimo,
  onFile,
}: {
  file: File | null;
  /** Le estensioni ammesse, con il punto: `.pdf`, `.jpg`. */
  estensioni?: string[];
  massimo?: number;
  onFile: (file: File | null) => void;
}) {
  const campo = useRef<HTMLInputElement>(null);
  const id = useId();
  const [sopra, setSopra] = useState(false);
  const [problema, setProblema] = useState<string | null>(null);

  const accetta = (scelto: File | null) => {
    setProblema(null);
    if (!scelto) return onFile(null);

    const punto = scelto.name.lastIndexOf('.');
    const estensione = punto > 0 ? scelto.name.slice(punto).toLowerCase() : '';
    if (estensioni && estensioni.length > 0 && !estensioni.includes(estensione)) {
      setProblema(
        t('«{estensione}» non è tra i formati ammessi: {ammessi}.', {
          estensione: estensione || t('senza estensione'),
          ammessi: estensioni.join(', '),
        }),
      );
      return;
    }
    if (massimo && scelto.size > massimo) {
      setProblema(t('Il file pesa {peso}: il limite è {limite}.', { peso: peso(scelto.size), limite: peso(massimo) }));
      return;
    }
    onFile(scelto);
  };

  if (file) {
    return (
      <div className="zona-file zona-file-pieno">
        <div>
          <strong>{file.name}</strong>
          <span className="dim" style={{ marginLeft: 8 }}>
            {peso(file.size)}
          </span>
        </div>
        <button className="btn btn-ghost" onClick={() => accetta(null)}>
          {t('Togli')}
        </button>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        className={sopra ? 'zona-file zona-file-sopra' : 'zona-file'}
        aria-describedby={id}
        onClick={() => campo.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setSopra(true);
        }}
        onDragLeave={() => setSopra(false)}
        onDrop={(e) => {
          e.preventDefault();
          setSopra(false);
          accetta(e.dataTransfer.files[0] ?? null);
        }}
      >
        <span>{t('Trascina qui il file, o fai clic per sceglierlo')}</span>
        <span className="dim" id={id} style={{ fontSize: 12 }}>
          {estensioni && estensioni.length > 0 ? estensioni.join(' · ') : t('qualsiasi formato')}
          {massimo ? ` — fino a ${peso(massimo)}` : ''}
        </span>
      </button>
      <input
        ref={campo}
        type="file"
        hidden
        accept={estensioni?.join(',')}
        onChange={(e) => accetta(e.target.files?.[0] ?? null)}
      />
      {problema && (
        <div className="avviso" role="alert" style={{ marginTop: 8 }}>
          <span className="avviso-segno">▲</span>
          <span>{problema}</span>
        </div>
      )}
    </>
  );
}
